import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as actionService from '../services/actionService';
import { parseError } from '../utils/errorMessages';

const ActionContext = createContext(null);

export const useAction = () => {
  const context = useContext(ActionContext);
  if (!context) throw new Error('useAction must be used within ActionProvider');
  return context;
};

export const ActionProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const [actions, setActions] = useState([]);
  const [counts, setCounts] = useState({ total: 0, critical: 0, warning: 0, info: 0 });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const fetchActions = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await actionService.getActionFeed(authenticatedFetch, filters);
      setActions(data.items);
      setCounts({
        total: data.total,
        critical: data.critical,
        warning: data.warning,
        info: data.info,
      });
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const triggerGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await actionService.generateActions(authenticatedFetch);
      // Refresh feed after generation
      await fetchActions();
      return result;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authenticatedFetch, fetchActions]);

  const resolveItem = useCallback(async (actionId) => {
    try {
      await actionService.resolveAction(authenticatedFetch, actionId);
      setActions(prev => prev.filter(a => a.id !== actionId));
      setCounts(prev => {
        const item = actions.find(a => a.id === actionId);
        if (!item) return prev;
        return {
          total: prev.total - 1,
          critical: prev.critical - (item.severity === 'critical' ? 1 : 0),
          warning: prev.warning - (item.severity === 'warning' ? 1 : 0),
          info: prev.info - (item.severity === 'info' ? 1 : 0),
        };
      });
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, actions]);

  const bulkResolve = useCallback(async (actionIds) => {
    try {
      const result = await actionService.bulkResolveActions(authenticatedFetch, actionIds);
      setActions(prev => prev.filter(a => !actionIds.includes(a.id)));
      // Re-compute counts from remaining items
      setActions(prev => {
        const newCounts = { total: 0, critical: 0, warning: 0, info: 0 };
        prev.forEach(a => {
          newCounts.total += 1;
          if (a.severity === 'critical') newCounts.critical += 1;
          else if (a.severity === 'warning') newCounts.warning += 1;
          else newCounts.info += 1;
        });
        setCounts(newCounts);
        return prev;
      });
      return result;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const value = {
    actions,
    counts,
    loading,
    generating,
    error,
    fetchActions,
    triggerGenerate,
    resolveItem,
    bulkResolve,
  };

  return (
    <ActionContext.Provider value={value}>
      {children}
    </ActionContext.Provider>
  );
};
